// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  Component,
  ViewChild,
  input,
  computed,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lfx-hover-trigger',
  imports: [CommonModule, NgbPopoverModule],
  templateUrl: './hover-trigger.component.html',
  styleUrls: ['./hover-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class HoverTriggerComponent {
  @ViewChild('popoverRef', { static: true }) readonly popover!: NgbPopover;

  /**
   * Placement of the popover relative to the trigger element
   * @default 'auto'
   */
  readonly placement = input<string>('auto');

  /**
   * Custom CSS class to apply to the popover
   */
  readonly popoverClass = input<string>('');

  /**
   * Custom container for the popover
   * @default 'body'
   */
  readonly container = input<string>('body');

  /**
   * Whether to disable the popover
   * @default false
   */
  readonly disabled = input<boolean>(false);

  /**
   * Triggers for the popover
   * @default 'hover focus'
   */
  readonly triggers = input<string>('hover focus');

  /**
   * Delay before showing the popover (in ms)
   * @default 100
   */
  readonly openDelay = input<number>(100);

  /**
   * Delay before hiding the popover (in ms)
   * @default 100
   */
  readonly closeDelay = input<number>(100);

  /**
   * Whether to make the popover sticky when hovering over its content
   * Useful for interactive content with buttons, links, etc.
   * @default false
   */
  readonly sticky = input<boolean>(false);

  /**
   * Cursor type to display on hover
   * @default 'help'
   */
  readonly cursor = input<'help' | 'pointer' | 'default'>('help');

  /**
   * Theme for the popover styling
   * @default 'light'
   */
  readonly theme = input<'dark' | 'light'>('light');

  // Internal state using signals
  private readonly closeTimeoutId = signal<number | null>(null);

  /**
   * Get the effective triggers based on sticky mode
   * When sticky is true, we disable ngBootstrap's default triggers
   * and handle the behavior manually
   */
  protected readonly effectiveTriggers = computed(() =>
    this.sticky() ? '' : this.triggers()
  );

  /**
   * Computed popover class that combines user class with theme class
   */
  protected readonly effectivePopoverClass = computed(() => {
    const userClass = this.popoverClass();
    const themeClass = this.theme() === 'dark' ? 'popover-dark' : '';
    return [userClass, themeClass].filter(Boolean).join(' ');
  });

  /**
   * Show the popover (used for sticky behavior)
   */
  protected showPopover(): void {
    if (this.disabled()) return;

    // Clear any pending close timeout
    const timeoutId = this.closeTimeoutId();
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.closeTimeoutId.set(null);
    }

    if (this.sticky()) {
      this.popover.open();
    }
  }

  /**
   * Hide the popover with optional delay (used for sticky behavior)
   */
  protected hidePopover(immediate = false): void {
    if (immediate) {
      const timeoutId = this.closeTimeoutId();
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.closeTimeoutId.set(null);
      }
      this.popover.close();
      return;
    }

    if (this.sticky()) {
      const timeoutId = window.setTimeout(() => {
        this.popover.close();
        this.closeTimeoutId.set(null);
      }, this.closeDelay());

      this.closeTimeoutId.set(timeoutId);
    }
  }

  /**
   * Cancel hide when re-entering trigger or popover content
   */
  protected cancelHide(): void {
    const timeoutId = this.closeTimeoutId();
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.closeTimeoutId.set(null);
    }
  }
}
